import dayjs from 'dayjs';
import { Fragment, useState } from 'react';

import { Checkbox, Divider, Flex, Skeleton, Text } from '@mantine/core';

import { useQuery } from '@tanstack/react-query';

import { selectPersonId } from '@/store/user-process/user-selectors';

import { bookingApi } from '@/shared/api/booking';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { LabelInput } from '@/shared/uikit/components/input';

import { PlaceTakenSelection } from './types';

import { useAppSelector } from '@/hooks';

type PlaceSelectSheetProps = {
	opened: boolean;
	onClose: () => void;
	onSelect: (selection: PlaceTakenSelection) => void;
};

const MANUAL_KEY = 'manual';

const cleanName = (name?: string) => name?.replace(/\s\|\s[\d\D]+/gm, '') ?? '';

export const PlaceSelectSheet = ({ opened, onClose, onSelect }: PlaceSelectSheetProps) => {
	const personId = useAppSelector(selectPersonId);

	const { data, isLoading } = useQuery({
		queryKey: ['car_bookings', personId],
		queryFn: () => bookingApi.getCarBookingsForClient(personId as number),
		enabled: opened && !!personId,
	});

	const [selectedKey, setSelectedKey] = useState<string | null>(null);
	const [manualValue, setManualValue] = useState('');

	const bookings = data?.bookings ?? [];
	const personalObjects = data?.personal_objects ?? [];

	const isManualSelected = selectedKey === MANUAL_KEY;
	const isNextDisabled = !selectedKey || (isManualSelected && !manualValue.trim());

	const handleSelect = (key: string) => {
		setSelectedKey((prev) => (prev === key ? null : key));
	};

	const buildSelection = (): PlaceTakenSelection | null => {
		if (selectedKey === MANUAL_KEY) {
			const manualPlace = manualValue.trim();

			return {
				name: manualPlace,
				isPersonal: false,
				manualPlace,
			};
		}

		const booking = bookings.find((item) => `b-${item.id}` === selectedKey);
		if (booking) {
			return {
				name: cleanName(booking.booking_object?.name),
				isPersonal: false,
				bookingId: booking.id,
				bookingObjectId: booking.booking_object_id,
				start: booking.start,
				end: booking.end,
			};
		}

		const personal = personalObjects.find((item) => `p-${item.id}` === selectedKey);
		if (personal) {
			return {
				name: cleanName(personal.name),
				isPersonal: true,
				bookingObjectId: personal.id,
			};
		}

		return null;
	};

	const handleNext = () => {
		const selection = buildSelection();
		if (selection) onSelect(selection);
	};

	return (
		<BottomSheet
			opened={opened}
			onClose={onClose}
			title="Какое место занято?"
			withBackButton
			withCloseButton={false}
			isFullScreen
			buttons={[
				{
					children: 'Далее',
					onClick: handleNext,
					...(isNextDisabled ? { variant: 'light', disabled: true } : {}),
				},
			]}
		>
			<Skeleton radius={24} visible={isLoading}>
				<Flex direction="column" gap={8} py="xs">
					{bookings.map((booking) => {
						const key = `b-${booking.id}`;

						return (
							<Fragment key={key}>
								<Flex
									justify="space-between"
									align="center"
									gap={8}
									onClick={() => handleSelect(key)}
									style={{ cursor: 'pointer' }}
								>
									<Flex direction="column" maw={200}>
										<Text size="md" fw={600}>
											{cleanName(booking.booking_object?.name)}
										</Text>
										<Text c="dimmed" size="sm" fw={500}>
											{booking.booking_object.type?.name}
										</Text>
									</Flex>
									<Flex align="center" gap={5}>
										<Flex direction="column" ta="right">
											<Text c="dimmed" size="sm" fw={500}>
												{`С ${dayjs(booking.start).format('HH.mm')} до ${dayjs(booking.end).format('HH.mm')}`}
											</Text>
											<Text c="dimmed" size="sm" fw={500}>
												{dayjs(booking.start).format('DD MMMM')}
											</Text>
										</Flex>
										<Checkbox radius="xl" checked={selectedKey === key} readOnly />
									</Flex>
								</Flex>
								<Divider color="#6670851A" />
							</Fragment>
						);
					})}

					{personalObjects.map((object) => {
						const key = `p-${object.id}`;

						return (
							<Fragment key={key}>
								<Flex
									justify="space-between"
									align="center"
									gap={8}
									onClick={() => handleSelect(key)}
									style={{ cursor: 'pointer' }}
								>
									<Flex direction="column" maw={240}>
										<Text size="md" fw={600}>
											{cleanName(object.name)}
										</Text>
										<Text c="dimmed" size="sm" fw={500}>
											Персональное место
										</Text>
									</Flex>
									<Checkbox radius="xl" checked={selectedKey === key} readOnly />
								</Flex>
								<Divider color="#6670851A" />
							</Fragment>
						);
					})}

					<Flex justify="space-between" align="center" gap={8}>
						<LabelInput
							style={{ flex: 1 }}
							label="Указать место вручную"
							placeholder="Введите название места"
							value={manualValue}
							onFocus={() => setSelectedKey(MANUAL_KEY)}
							onChange={(event) => {
								setManualValue(event.currentTarget.value);
								setSelectedKey(MANUAL_KEY);
							}}
						/>
						<Checkbox
							radius="xl"
							checked={isManualSelected}
							onChange={() => handleSelect(MANUAL_KEY)}
						/>
					</Flex>
				</Flex>
			</Skeleton>
		</BottomSheet>
	);
};
